import React, { forwardRef, useState, useEffect, useMemo } from "react";
import { Autocomplete, Chip, Box, Stack } from "@mui/material";
import TextField from "components/CustomTextField";
import styled from "@emotion/styled";

export const StyledSelectedChip = styled(Chip)(({ theme }) => ({
  width: "200px",
  borderRadius: "10px",
  fontSize: 12,
  display: "flex",
  justifyContent: "space-between",
}));
export default forwardRef(function CustomAutocomplete(
  {
    label,
    value,
    options = [],
    optionValue = "value",
    optionLabel = "label",
    optionGroupBy = "group",
    variant = "filled",
    fullWidth = true,
    limit,
    onChange,
    chipHeight,
    chipProps,
    filterSelectedOptions = true,
    ...props
  },
  ref
) {
  const [disabled, setDisabled] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const filterOption = (value) => {
    setInputValue(value);
    const startResult = options.filter((opt) =>
      opt?.[optionLabel]?.toLowerCase()?.startsWith(value?.toLowerCase())
    );
    const containResult = options.filter(
      (opt) =>
        !opt?.[optionLabel]?.toLowerCase()?.startsWith(value?.toLowerCase()) &&
        opt?.[optionLabel]?.toLowerCase().includes(value?.toLowerCase())
    );
    return [...startResult, ...containResult];
  };
  const filteredOptions = useMemo(() => filterOption(inputValue), [inputValue]);
  useEffect(() => {
    if (!!limit) {
      if (value?.length > 0 && options?.length > 0) {
        const newValue = value
          ?.filter((v) => v)
          ?.map((v) =>
            typeof v !== "object"
              ? options.find((o) => o?.[optionValue] == v) ?? v
              : v
          );

        if (JSON.stringify(newValue) !== JSON.stringify(value)) {
          onChange(newValue);
        }
      }
    } else {
      if (value) {
        const newValue =
          typeof value !== "object"
            ? options?.find((o) => o?.[optionValue] == value) ?? value
            : value;
        if (JSON.stringify(newValue) !== JSON.stringify(value)) {
          onChange(newValue);
        }
      }
    }
  }, [value, options]);

  useEffect(() => {
    if (limit && value?.length >= limit) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [value?.length]);

  const onChipDelete = (v, i) => {
    if (Array.isArray(value)) {
      const newValue = value?.filter((item, i) => item.id !== v.id);
      onChange(newValue);
    } else {
      if (v == value) {
        onChange([]);
      }
    }
  };
  return (
    <>
      <Autocomplete
        ref={ref}
        value={value}
        options={inputValue ? filteredOptions : options}
        fullWidth={fullWidth}
        multiple={!!limit}
        onInputChange={(e, newValue) => setInputValue(newValue)}
        onChange={(e, value) => {
          if (!limit || (limit && value?.length <= limit)) {
            return onChange(
              Array.isArray(value)
                ? value.map((v) =>
                    typeof v !== "object" ? v?.[optionValue] : v ?? ""
                  )
                : value?.[optionValue] ?? ""
            );
          }
        }}
        groupBy={(o) => o?.[optionGroupBy]}
        isOptionEqualToValue={(o, v) => o?.[optionValue] == v?.[optionValue]}
        getOptionLabel={(o) =>
          (typeof o !== "object"
            ? options.find((opt) => opt[optionValue] === o)?.[optionLabel]
            : o?.[optionLabel]) ?? ""
        }
        getOptionDisabled={(option) =>
          limit && (value?.length === limit || value?.includes(option))
        }
        renderInput={(params) => (
          <TextField {...params} label={label} {...props} disabled={disabled} />
        )}
        renderTags={(tagValue, getTagProps) => (
          <>
            {variant !== "chip" &&
              tagValue?.map((option, index) => (
                <Chip
                  color="primary"
                  key={index}
                  label={option?.[optionLabel]}
                  {...chipProps}
                  {...getTagProps({ index })}
                />
              ))}
          </>
        )}
        filterSelectedOptions={filterSelectedOptions}
        disableClearable={variant === "chip"}
        {...props}
      />
      {variant === "chip" && (
        <Stack gap={1} sx={{ mt: 1 }}>
          {value &&
            (Array.isArray(value) ? value : [value])?.map((v, i) => (
              <StyledSelectedChip
                color="primary"
                key={i}
                label={v?.[optionLabel]}
                {...chipProps}
                onDelete={() => onChipDelete(v, i)}
              />
            ))}
        </Stack>
      )}
    </>
  );
});
